/**
 * Phone number utility functions
 * 
 * Centralizes phone number handling from REACT_APP_CALL_NOW environment variable
 * with a default fallback value.
 */

const DEFAULT_PHONE_NUMBER = '14255461794';

/**
 * Gets the phone number from environment variable or returns the default
 * @returns Phone number as a string (digits only, no formatting)
 */
export function getPhoneNumber(): string {
  return process.env.REACT_APP_CALL_NOW || DEFAULT_PHONE_NUMBER;
}

/**
 * Formats a phone number for display with parentheses and dashes
 * Expects a 11-digit phone number (e.g., "14255461794")
 * @param phone - Phone number string (digits only)
 * @returns Formatted phone number (e.g., "(425) 546-1794")
 */
export function formatPhoneForDisplay(phone?: string): string {
  const phoneNumber = phone || getPhoneNumber();
  // Remove any non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Expect 11 digits (1 + area code + number)
  if (digits.length === 11) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  
  // Fallback: if 10 digits, assume no country code
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  // If format doesn't match, return as-is
  return phoneNumber;
}

/**
 * Formats a phone number for tel: links
 * Adds the + prefix if not already present
 * @param phone - Phone number string (digits only)
 * @returns Formatted phone number for tel: link (e.g., "+14255461794")
 */
export function formatPhoneForTel(phone?: string): string {
  const phoneNumber = phone || getPhoneNumber();
  // Remove any non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Add + prefix if not already present
  return digits.startsWith('+') ? digits : `+${digits}`;
}

